import React from 'react';
import { App, Button, Card, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';

const ContractFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { options: empOptions } = useEmployeeOptions();

  const save = async () => {
    try {
      const v = await form.validateFields();
      const created = await hrmsService.createContract({
        ...v,
        effective_from: v.effective_from.format('YYYY-MM-DD'),
        effective_to: v.effective_to ? v.effective_to.format('YYYY-MM-DD') : undefined,
        probation_end_date: v.probation_end_date ? v.probation_end_date.format('YYYY-MM-DD') : undefined,
      });
      message.success('Contract created');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.contracts() });
      navigate(`/hr/contracts/${created?.id || ''}`);
    } catch {
      message.error('Create failed');
    }
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="New employment contract"
          subtitle="Versioned contract terms"
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/contracts')}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={save}>
                Save
              </Button>
            </Space>
          }
        />
      }
    >
      <Form form={form} layout="vertical" initialValues={{ contract_type: 'Limited', status: 'Draft' }}>
        <Card title="Contract">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
                <Select options={empOptions} showSearch optionFilterProp="label" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="contract_number" label="Contract number" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="contract_type" label="Type">
                <Select
                  options={['Limited', 'Unlimited', 'PartTime', 'Probation'].map((v) => ({ value: v, label: v }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="basic_salary" label="Basic salary">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="effective_from" label="Effective from" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="effective_to" label="Effective to">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="probation_end_date" label="Probation end">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="work_location" label="Work location">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </WorkspaceLayout>
  );
};

export default ContractFormPage;
